import React, {useEffect, useState} from 'react';
import {DataTypeErrorCode} from "../../../../../common/apis/datatype/DataTypeApiClient";
import {ConstraintDataDto, ConstraintType, DataTypeConstraintCreateDto} from "../../../../../common/apis/datatype/DataTypeConstraintDto";
import {CoreDataType} from "../../../../../common/apis/datatype/DataTypeDefinitionDto";
import {ValidationError} from "../../../../../common/ValidationError";
import {Button} from "../../../../../components/button/Button";
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from '../../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import Grid from '../../../../../components/dialogs/Grid';
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import TextField from '../../../../../components/fields/textfield/TextField';
import {t} from '../../../../../store/localization/TranslMessasge';
import {CommonTranslationKey} from '../../CommonTranslationKey';
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {DataTypeTranslationKey} from '../DataTypeTranslationKey';
import {ConstraintDataPluginRegistry} from './ConstraintDataPluginRegistry';
import {ConstraintTypeFormatter} from "./ConstraintTypeFormatter";

interface DataTypeConstraintCreateDialogProps {
    open: boolean;
    coreDataType: CoreDataType;
    onCreateConstraint: (constraint: DataTypeConstraintCreateDto) => Promise<void>;
    onClose: (created: boolean) => void;
}

export function DataTypeConstraintCreateDialog(
    {open, coreDataType, onCreateConstraint, onClose}: Readonly<DataTypeConstraintCreateDialogProps>) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [constraintType, setConstraintType] = useState<ConstraintType | undefined>(undefined);
    const [constraintData, setConstraintData] = useState<ConstraintDataDto | undefined>(undefined);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        if (open) {
            setName('');
            setDescription('');
            const constraintTypeOptions = ConstraintDataPluginRegistry.getAvailableTypesByDataType(coreDataType);
            const defaultConstraintType = constraintTypeOptions.length > 0 ? constraintTypeOptions[0] : undefined;
            setConstraintType(defaultConstraintType);
            if (defaultConstraintType) {
                const constraintPlugin = ConstraintDataPluginRegistry.getPlugin(defaultConstraintType);
                if (constraintPlugin) {
                    setConstraintData(constraintPlugin.getInitialData());
                }
            }
            setErrors({});
        }
    }, [open, coreDataType]);

    function changeConstraintType(newType: ConstraintType | null) {
        setConstraintType(newType ?? undefined);
        const constraintPlugin = newType ? ConstraintDataPluginRegistry.getPlugin(newType) : undefined;
        if (constraintPlugin) {
            setConstraintData(constraintPlugin.getInitialData());
            setErrors({});
        }
    }

    const createConstraint = async () => {
        if (!validateForm() || !constraintData) {
            return;
        }

        setInProgress(true);

        const createDto: DataTypeConstraintCreateDto = {
            name: name.trim(),
            description: description.trim() || undefined,
            constraintData: constraintData
        };

        onCreateConstraint(createDto)
            .then(
                () => {
                    setErrors({});
                    onClose(true);
                },
                (error) => {
                    if (error instanceof ValidationError) {
                        handleValidationErrors(error);
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                    }
                }
            )
            .finally(() => setInProgress(false));
    };

    function handleValidationErrors(error: ValidationError) {
        const newErrors: Record<string, string> = {};

        if (error.error.code === DataTypeErrorCode.CONSTRAINTS_CONFLICTING_NAME) {
            newErrors.name = t(DataTypeTranslationKey.CONSTRAINTS_NAMES_MUST_BE_UNIQUE);
        }
        setErrors(newErrors);
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};

        if (!name.trim()) {
            newErrors.name = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        if (!constraintType) {
            newErrors.constraintType = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        } else if (constraintData) {
            const constraintPlugin = ConstraintDataPluginRegistry.getPlugin(constraintType);
            if (constraintPlugin) {
                const constraintErrors = constraintPlugin.validate(constraintData, coreDataType);
                Object.assign(newErrors, constraintErrors);
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const constraintTypeOptions = ConstraintDataPluginRegistry.getAvailableTypesByDataType(coreDataType);
    const constraintPlugin = (constraintData && constraintType) ? ConstraintDataPluginRegistry.getPlugin(constraintType) : null;

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle title={t(DataTypeTranslationKey.NEW_CONSTRAINT)}
                         onDialogClosed={() => onClose(false)}/>
            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <TextField
                                label={t(DataTypeTranslationKey.NAME)}
                                value={name}
                                onChange={setName}
                                required={true}
                                errorMessage={errors.name}
                                fullWidth autoFocus/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                                label={t(CommonTranslationKey.DESCRIPTION)}
                                value={description}
                                onChange={setDescription}
                                multiline
                                rows={3}
                                fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                        <SingleSelectField id={"constraint-type"}
                                label={t(DataTypeTranslationKey.CONSTRAINT_TYPE)}
                                value={constraintType}
                                onChange={value => changeConstraintType(value)}
                                options={constraintTypeOptions}
                                getOptionLabel={(option) => ConstraintTypeFormatter.formatConstraintType(option, t)}
                                required={true}
                                // clearable={false}
                                errorMessage={errors.constraintType} />
                    </Grid>
                    {constraintPlugin && constraintData && (
                        <Grid item xs={12}>
                            {constraintPlugin.render({
                                data: constraintData,
                                coreDataType: coreDataType,
                                errors: errors,
                                onChange: setConstraintData
                            })}
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <ButtonLayout>
                    <PositiveButtons>
                        <Button label={t(CommonTranslationKey.CREATE)}
                                variant="contained"
                                onClick={createConstraint}
                                disabled={inProgress}/>
                    </PositiveButtons>
                    <NegativeButtons>
                        <CancelButton onClick={onClose} disabled={inProgress}/>
                    </NegativeButtons>
                </ButtonLayout>
            </DialogActions>
        </Dialog>
    );
}
